import React, {useEffect, useId, useRef, useState} from "react";

import {
    IConfigDataConfigLoadingConfig,
    ITableDataEntry,
    ITableDataEntryValue,
    LoadingConfig,
    ProductTemplate
} from "./utils/interfaces";
import EditDataCell from "./components/templates/table-components/edit-datacell";
import {ProgressSpinner} from "primereact/progressspinner";
import {CellType, TemplateLoadingType} from "./utils/enums";
import {Button} from "primereact/button";
import {Toast, ToastSeverityType} from "primereact/toast";

import "@js-joda/core";

import {getTemplateLoadingRange} from "./utils/timeUtils";

// Styles
import './ProductTemplateTable.scss';

import {dateWithoutTimeZone, formatDateTimeOrNow} from "utils/time-utils";
import ProductTemplateTableDefault from "./ProductTemplateTableDefault";
import ProductTemplateTableTransposed from "./ProductTemplateTableTransposed";
import {ExportDataTableButton} from "components/basic/export-data-table/ExportDataTable";
import {Dashboard} from "components/pages/dashboard/components/interfaces";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import {
    useCreateProductTemplateEntry,
    useDeleteProductTemplateEntry,
    useGetProductTemplateConfig,
    useGetProductTemplateData,
    useGetProductTemplateFilterPreviewData,
    useSignProductTemplateEntry,
    useUpdateProductTemplateEntry
} from "api/api-hooks/product-templates-hooks";
import IComponentBaseProps = Dashboard.IComponentBaseProps;
import IComponentTimed = Dashboard.IComponentTimed;

export interface ReportOptions {
    splitAfter?: number;
    fontSize?: string;
}

export interface IProductTemplateTableProps extends IComponentBaseProps<ReportOptions>, IComponentTimed, ProductTemplate.IHorizontalCommonConfig {
    safeName: string;
    token?: string;
    customHost?: string;
    transposed?: boolean;
    showTitle?: boolean;
    updateFrequency?: number;
    hiddenColumns?: Array<string>;
    onlyShownColumns?: Array<string>;
    itemsPerPage?: number;
    overrideLoadingConfig?: LoadingConfig;
    filter?: string;

    onFetchError?: (val: string) => void;
    retry?: boolean | number;

    exactDateFrom?: Date;
    exactDateTo?: Date;
    hideDates?: boolean;
    useExternalFilterEndpoint?: boolean;
    allowAddEntry?: boolean;
    allowRowEdit?: boolean;
}

export default function ProductTemplateTable(props: IProductTemplateTableProps) {
    const id = useId();
    const loadingType = props.overrideLoadingConfig?.loadingType;
    const allowRowEdit = props.allowRowEdit !== undefined ? props.allowRowEdit : true;

    const transposed = getBoolean(props.transposed);
    const showTitle = props.showTitle != null ? props.showTitle : true;

    const toast = useRef<Toast | null>(null);

    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const [datesChanged, setDatesChanged] = useState<boolean>(false);

    const [allowAddEntry, setAllowAddEntry] = useState<boolean>(allowRowEdit);
    const [allowDelete, setAllowDelete] = useState<boolean>(true);

    const [disabledDatesFrom, setDisabledDatesFrom] = useState<Date[] | undefined>([]);
    const [disabledDatesTo, setDisabledDatesTo] = useState<Date[] | undefined>([]);

    const [selectedEntryIndex, setSelectedEntryIndex] = useState<number | undefined>(undefined);

    const [selectedEntryId, setSelectedEntryId] = useState<string | undefined>('');
    const [selectedEntryBody, setSelectedEntryBody] = useState<ITableDataEntry | undefined>(undefined);

    const [page, setPage] = useState(0);

    const [updateFrequency, setUpdateFrequency] = useState<number | boolean>(props.updateFrequency ? props.updateFrequency * 1000 : false);

    const getProductTemplateDataSelectedFunction = props.useExternalFilterEndpoint ? useGetProductTemplateFilterPreviewData : useGetProductTemplateData;

    const {mutate: createNewEntry, status: createNewEntryStatus} = useCreateProductTemplateEntry(
        {
            safeName: props.safeName,
            dateFrom: dateWithoutTimeZone(dateFrom).toISOString(),
            dateTo: dateWithoutTimeZone(dateTo).toISOString(),
            filter: props.filter,
        },
        {
            onSuccess: () => {
                notifySuccess('Created');
            },
            onMutate: () => {
                notifySuccess('Creating...');
            },
            onFailure: () => {
                notifyError('Failed to create entry!');
            },
        }
    );
    const {mutate: updateNewEntry} = useUpdateProductTemplateEntry(
        {
            safeName: props.safeName,
            dateFrom: dateWithoutTimeZone(dateFrom).toISOString(),
            dateTo: dateWithoutTimeZone(dateTo).toISOString(),
            filter: props.filter,
        },
        {
            onSuccess: () => {
                notifySuccess('Updated');
            },
            onMutate: () => {
                notifySuccess('Updating...');
            },
            onFailure: () => {
                notifyError('Failed to update entry!');
            },
        }
    );
    const {mutate: deleteEntry} = useDeleteProductTemplateEntry(
        {
            safeName: props.safeName,
            dateFrom: dateWithoutTimeZone(dateFrom).toISOString(),
            dateTo: dateWithoutTimeZone(dateTo).toISOString(),
            filter: props.filter,
        },
        {
            onSuccess: () => {
                notifySuccess('Deleted');
            },
            onMutate: () => {
                notifySuccess('Deleting...');
            },
            onFailure: () => {
                notifyError('Failed to delete entry!');
            },
        }
    );

    const {mutate: signEntryData} = useSignProductTemplateEntry(
        {
            safeName: props.safeName,
            dateFrom: dateWithoutTimeZone(dateFrom).toISOString(),
            dateTo: dateWithoutTimeZone(dateTo).toISOString(),
            filter: props.filter,
        },
        {
            onSuccess: () => {
                notifySuccess('Updated');
                refetchTemplateData();
            },
            onMutate: () => {
                notifySuccess('Updating...');
            },
            onFailure: () => {
                notifyError('Failed to update entry!');
            },
        }
    );

    const {
        data: configData,
        isError: configError,
        isLoading: isLoadingConfig,
    } = useGetProductTemplateConfig(props.safeName, id, {
        usePromiseHandler: (res: any) => {
            if (res.error) {
                console.log(res.error.message);
                throw res.error;
            }

            const {data} = res;

            if (props.hiddenColumns && props.hiddenColumns.length > 0) {
                const attributes = data.data.attributes.filter((attribute: any) => {
                    return !props.hiddenColumns?.includes(attribute.referenceName);
                });

                return {
                    ...data,
                    data: {
                        ...data.data,
                        attributes,
                    },
                };
            } else if (props.onlyShownColumns && props.onlyShownColumns.length > 0) {
                const attributes = data.data.attributes.filter((attribute: any) => {
                    return props.onlyShownColumns?.includes(attribute.referenceName);
                });

                return {
                    ...data,
                    data: {
                        ...data.data,
                        attributes,
                    },
                };
            }

            return data;
        },
    });

    const {
        data: templateData,
        isError: templateError,
        isLoading: isLoadingTemplate,
        refetch: refetchTemplateData,
    } = getProductTemplateDataSelectedFunction(
        {
            safeName: props.safeName,
            dateFrom: dateWithoutTimeZone(dateFrom).toISOString(),
            dateTo: dateWithoutTimeZone(dateTo).toISOString(),
            filter: props.filter,
        },
        {
            enabled: !!(configData && dateFrom && dateTo),
            refetchInterval: updateFrequency,
            retry: false,
            onError: (er) => {
                if (props.onFetchError) {
                    props.onFetchError(er.message);
                }
            },
        }
    );

    useEffect(() => {
        if (!isLoadingConfig && configData) {
            setAllowAddEntry(
                props.allowAddEntry !== undefined ? props.allowAddEntry : configData.data.config.allowRowAddition != null ? configData.data.config.allowRowAddition : true
            );
            setAllowDelete(configData.data.config.allowRowDeletion != null ? configData.data.config.allowRowDeletion : true);
            setDatesByConfigAndLoadingType();
        }
    }, [configData]);

    useEffect(() => {
        if (dateFrom) {
            disableDateDays(dateFrom, -1, setDisabledDatesTo);
        }
        if (dateTo) {
            disableDateDays(dateTo, 1, setDisabledDatesFrom);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => {
        if (props.exactDateFrom) {
            setDateFrom(props.exactDateFrom);
        }
        if (props.exactDateTo) {
            setDateTo(props.exactDateTo);
        }
    }, [props.exactDateFrom, props.exactDateTo]);

    function getBoolean(input: boolean | string | number | undefined): boolean {
        if (typeof input === 'boolean') {
            return input;
        }

        if (typeof input === 'string') {
            return input === 'true';
        }

        if (typeof input === 'number') {
            return input !== 0;
        }
        return false;
    }

    const setDatesByConfigAndLoadingType = () => {
        if (configData && !props.exactDateFrom && !props.exactDateTo) {
            const loadingType: TemplateLoadingType = props.overrideLoadingConfig?.loadingType ? props.overrideLoadingConfig.loadingType : configData.data.config.loadingType;
            const loadingConfig: IConfigDataConfigLoadingConfig = {...configData.data.config.loadingConfig, ...props.overrideLoadingConfig?.loadingConfig};

            const {start, end} = getTemplateLoadingRange(
                {
                    loadingType: loadingType,
                    loadingConfig: loadingConfig,
                },
                props.referenceTime
            );

            setDateFrom(start);
            setDateTo(end);
        }
    };

    const disableDateDays = (initDate: Date, step: number, setter: CallableFunction) => {
        let date = new Date(initDate);
        let year = date.getFullYear();
        let all_days = [];
        while (date.getFullYear() === year - 1 || date.getFullYear() === year || date.getFullYear() === year + 1) {
            let d = date.getFullYear() + '-' + (date.getMonth() + 1).toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0');
            all_days.push(new Date(d));
            date.setDate(date.getDate() + step);
        }
        setter(all_days);
    };

    const editedDataHandler = (indexEntry: number, refName: string, newValue: any, newCell?: ITableDataEntryValue) => {
        if (selectedEntryId && selectedEntryBody) {
            let tempEntry = {...selectedEntryBody};

            if (tempEntry.entries[refName] != null) {
                tempEntry.entries[refName].value = newValue;
            } else {
                if (newCell) {
                    tempEntry.entries[newCell.referenceName] = newCell;
                }
            }
            setSelectedEntryBody(tempEntry);
        }
    };

    const manageRowEdit = (entryIndex: number, editable: boolean) => {
        if (editable && templateData && templateData.length >= entryIndex) {
            const offsetedIndex = entryIndex + page;

            setSelectedEntryBody(JSON.parse(JSON.stringify(templateData[offsetedIndex])));
            setSelectedEntryId(templateData[offsetedIndex].id);
            setSelectedEntryIndex(entryIndex);
            setUpdateFrequency(false);
        } else {
            if (!datesChanged) {
                setUpdateFrequency(props.updateFrequency ? props.updateFrequency * 1000 : false);
            }
            setSelectedEntryId(undefined);
            setSelectedEntryBody(undefined);
            setSelectedEntryIndex(undefined);
        }
    };

    const onClickEdit = (entryIndex: number) => {
        manageRowEdit(entryIndex, true);
    };

    const onClickCopy = (currentIndex: number) => {
        if (templateData && templateData.length > currentIndex) {
            const offsetedIndex = currentIndex + page;

            const entry = {...templateData[offsetedIndex]};
            let entryEntriesList = JSON.parse(JSON.stringify(Object.values(entry.entries)));

            entryEntriesList = entryEntriesList.filter((ob: any) => ob.type !== CellType.USER_SIGNATURE);

            createNewEntry({body: entryEntriesList});
        }
    };

    const onClickSave = (entryIndex: number) => {
        if (selectedEntryId && selectedEntryBody) {
            updateNewEntry({entryId: selectedEntryId, body: Object.values(selectedEntryBody.entries)});
        }
        manageRowEdit(entryIndex, false);
    };

    const onClickDelete = (entryIndex: number) => {
        const offsetedIndex = entryIndex + page;

        if (templateData && templateData[offsetedIndex].id != null) {
            deleteEntry({entryId: templateData[offsetedIndex].id!!});

            if (selectedEntryId != null || selectedEntryId !== '') {
                manageRowEdit(entryIndex, false);
            }
        } else {
            notifyError('Entry index not found');
        }
    };

    const onClickCancel = (entryIndex: number) => {
        manageRowEdit(entryIndex, false);
    };

    const onClickSign = (entryIndex: number, referenceName: string) => {
        if (templateData) {
            const offsetedIndex = entryIndex + page;

            let entryID = templateData[offsetedIndex].id;
            if (entryID != null) {
                signEntryData({entryId: entryID, referenceName: referenceName});
            }
        }
    };

    const onClickAddEntry = () => {
        let body: Array<any> = [];
        let dateString = formatDateTimeOrNow('yyyy-MM-dd HH:mm');

        if (configData) {
            let val = configData.data.attributes.find((ob: any) => ob.referenceName === configData.keyColumn);

            if (val) {
                if (val.type === 'DATETIME') {
                    body = [
                        {
                            referenceName: val.referenceName,
                            type: val.type,
                            value: dateString,
                        },
                    ];
                }
                createNewEntry({body: body});
            }
        }
    };

    const showToast = (severity: ToastSeverityType, summary: string, detail: string, lifetime: number = 10000) => {
        toast.current!!.show({severity: severity, summary: summary, detail: detail, life: lifetime});
    };

    const notifySuccess = (msg: string) => {
        showToast('success', 'Success', msg, 3000);
    };

    const notifyError = (msg: string) => {
        showToast('error', 'Error', msg);
    };

    function EditRowDataCell(val: any, index: number) {
        let disableEdit = '';

        if (
            configData &&
            configData.data.config.signatureAttribute &&
            val.entries[configData.data.config.signatureAttribute] &&
            val.entries[configData.data.config.signatureAttribute].value.signed
        ) {
            disableEdit = val.entries[configData.data.config.signatureAttribute].value.display;
        }

        return (
            <EditDataCell
                key={val.id}
                index={index}
                onClickCopy={onClickCopy}
                onClickEdit={onClickEdit}
                onClickDelete={onClickDelete}
                onClickSave={onClickSave}
                onClickCancel={onClickCancel}
                isInEditMode={selectedEntryId === val.id}
                deletable={allowDelete}
                signedBy={disableEdit}
            />
        );
    }

    const handleKeyEnterPress = () => {
        if (selectedEntryIndex != null) {
            onClickSave(selectedEntryIndex);
        }
    };

    const handleTableKeyDown = (key: string) => {
        if (key === 'Enter' && selectedEntryId) {
            handleKeyEnterPress();
        }
    };

    const DateToConfigInitializeButton = () => {
        if (datesChanged) {
            return (
                <div>
                    <Button
                        icon={'pi pi-history'}
                        style={{transform: 'scale(95%)'}}
                        onClick={() => {
                            setDatesChanged(false);
                            setDatesByConfigAndLoadingType();
                            setUpdateFrequency(props.updateFrequency ? props.updateFrequency * 1000 : false);
                        }}
                        tooltip={'Return default dates'}
                        disabled={selectedEntryId !== '' && selectedEntryIndex != null}
                    />
                </div>
            );
        }
        return <></>;
    };

    const printSplitColumns = () => {
        if (props.reportOptions && props.reportOptions.splitAfter) {
            const data = [...(templateData || [])];
            const resultArray = [];

            for (let i = 0; i < data.length; i += props.reportOptions.splitAfter) {
                const chunk = data.slice(i, i + props.reportOptions.splitAfter);
                resultArray.push(chunk);
            }

            return resultArray.map((smallChunkArray) => {
                return (
                    <ProductTemplateTableTransposed
                        configData={configData}
                        templateData={smallChunkArray}
                        editedDataHandler={editedDataHandler}
                        selectedEntryBody={selectedEntryBody}
                        selectedEntryIndex={selectedEntryIndex}
                        selectedEntryId={selectedEntryId}
                        onClickSign={onClickSign}
                        handleTableKeyDown={handleTableKeyDown}
                        itemsPerPage={props.itemsPerPage}
                        page={page}
                        setPage={setPage}
                        EditRowDataCell={EditRowDataCell}
                        reportOptions={props.reportOptions}
                    />
                );
            });
        }

        return <>Number of columns to break is not defined</>;
    };

    const printSplitRows = () => {
        if (props.reportOptions && props.reportOptions.splitAfter) {
            const data = [...(templateData || [])];
            const resultArray = [];

            for (let i = 0; i < data.length; i += props.reportOptions.splitAfter) {
                const chunk = data.slice(i, i + props.reportOptions.splitAfter);
                resultArray.push(chunk);
            }

            return resultArray.map((smallChunkArray) => {
                return (
                    <ProductTemplateTableDefault
                        configData={configData}
                        templateData={smallChunkArray}
                        editedDataHandler={editedDataHandler}
                        selectedEntryBody={selectedEntryBody}
                        selectedEntryIndex={selectedEntryIndex}
                        selectedEntryId={selectedEntryId}
                        onClickSign={onClickSign}
                        page={page}
                        setPage={setPage}
                        handleTableKeyDown={handleTableKeyDown}
                        EditRowDataCell={EditRowDataCell}
                        itemsPerPage={props.itemsPerPage}
                        reportOptions={props.reportOptions}
                        editColumnLocation={props.editColumnLocation}
                        allowRowEdit={allowRowEdit}
                    />
                );
            });
        }

        return <>Number of columns to break is not defined</>;
    };

    const calendarMinuteStep = 5;

    const ErrorOnTemplateBody = () => {
        if (configError || templateError) {
            return (
                <div className='demo-container p-p-4'>
                    <Error text={'No data found'}/>
                </div>
            );
        }
        return (
            <div className='demo-container p-p-4'>
                <ProgressSpinner className='p-d-block p-mx-auto'/>
            </div>
        );
    };

    if (!configData) {
        if (!isLoadingConfig && !isLoadingTemplate) {
            return (
                <div>
                    <Error text={'Something went wrong'}/>
                </div>
            );
        }
        return (
            <div>
                <div className='demo-container p-p-4'>
                    <ProgressSpinner className='p-d-block p-mx-auto'/>
                </div>
            </div>
        );
    }

    return (
        <div className={'product-template-full-window'}>
            <Toast ref={toast}/>
            <div className='product-template-wrapper'>
                {showTitle &&
                    <div className={'title-window non-printable'}>{configData && configData.displayName}</div>}

                <div className='product-template-header non-printable'>
                    {!props.hideDates && (
                        <>
                            {loadingType && loadingType === TemplateLoadingType.FIXED_PERIOD && (
                                <div className={'calendars-and-button-wrapper'}>
                                    <div className={'calendars-wrapper'}>
                                        Date from
                                        <CalendarWrapper
                                            id='time24'
                                            value={dateFrom}
                                            style={{paddingLeft: '10px'}}
                                            onChange={(e) => {
                                                setDateFrom(e);
                                                setDatesChanged(true);
                                                setUpdateFrequency(false);
                                            }}
                                            showTime
                                            disabled={selectedEntryId !== '' && selectedEntryIndex != null}
                                            dateFormat='yy-mm-dd'
                                            stepMinute={calendarMinuteStep}
                                        />
                                    </div>
                                    <DateToConfigInitializeButton/>
                                </div>
                            )}

                            {!loadingType && configData && configData.data.config.loadingType === TemplateLoadingType.FIXED_PERIOD && (
                                <div className={'calendars-and-button-wrapper'}>
                                    <div className={'calendars-wrapper'}>
                                        Date from
                                        <CalendarWrapper
                                            id='time24'
                                            value={dateFrom}
                                            style={{paddingLeft: '10px'}}
                                            onChange={(e) => {
                                                setDateFrom(e);
                                                setDatesChanged(true);
                                                setUpdateFrequency(false);
                                            }}
                                            showTime
                                            disabled={selectedEntryId !== '' && selectedEntryIndex != null}
                                            dateFormat='yy-mm-dd'
                                            stepMinute={calendarMinuteStep}
                                        />
                                    </div>
                                    <DateToConfigInitializeButton/>
                                </div>
                            )}

                            {loadingType && loadingType === TemplateLoadingType.RANGE && (
                                <div className={'calendars-and-button-wrapper'}>
                                    <div className={'calendars-wrapper'}>
                                        <div className={'calendars-wrapper'}>
                                            Date from
                                            <CalendarWrapper
                                                id='time24'
                                                value={dateFrom}
                                                style={{paddingLeft: '10px'}}
                                                onChange={(e) => {
                                                    setDateFrom(e);
                                                    setDatesChanged(true);
                                                    setUpdateFrequency(false);
                                                }}
                                                showTime
                                                dateFormat='yy-mm-dd'
                                                disabled={selectedEntryId !== '' && selectedEntryIndex != null}
                                                disabledDates={disabledDatesFrom}
                                                stepMinute={calendarMinuteStep}
                                            />
                                        </div>
                                        <div className={'calendars-wrapper'}>
                                            Date to
                                            <CalendarWrapper
                                                id='time24'
                                                value={dateTo}
                                                onChange={(e) => {
                                                    setDateTo(e);
                                                    setDatesChanged(true);
                                                    setUpdateFrequency(false);
                                                }}
                                                showTime
                                                dateFormat='yy-mm-dd'
                                                disabled={selectedEntryId !== '' && selectedEntryIndex != null}
                                                disabledDates={disabledDatesTo}
                                                stepMinute={calendarMinuteStep}
                                            />
                                        </div>
                                    </div>
                                    <DateToConfigInitializeButton/>
                                </div>
                            )}

                            {!loadingType && configData && configData.data.config.loadingType === TemplateLoadingType.RANGE && (
                                <div className={'calendars-and-button-wrapper'}>
                                    <div className={'calendars-wrapper'}>
                                        <div className={'calendars-wrapper'}>
                                            Date from
                                            <CalendarWrapper
                                                id='time24'
                                                value={dateFrom}
                                                style={{paddingLeft: '10px'}}
                                                onChange={(e) => {
                                                    setDateFrom(e);
                                                    setDatesChanged(true);
                                                    setUpdateFrequency(false);
                                                }}
                                                showTime
                                                dateFormat='yy-mm-dd'
                                                disabled={selectedEntryId !== '' && selectedEntryIndex != null}
                                                disabledDates={disabledDatesFrom}
                                                stepMinute={calendarMinuteStep}
                                            />
                                        </div>
                                        <div className={'calendars-wrapper'}>
                                            Date to
                                            <CalendarWrapper
                                                id='time24'
                                                value={dateTo}
                                                onChange={(e) => {
                                                    setDateTo(e);
                                                    setDatesChanged(true);
                                                    setUpdateFrequency(false);
                                                }}
                                                showTime
                                                dateFormat='yy-mm-dd'
                                                disabled={selectedEntryId !== '' && selectedEntryIndex != null}
                                                disabledDates={disabledDatesTo}
                                                stepMinute={calendarMinuteStep}
                                            />
                                        </div>
                                    </div>
                                    <DateToConfigInitializeButton/>
                                </div>
                            )}
                        </>
                    )}

                    {/*// Justification space between. PlaceHolder */}
                    <div></div>

                    {configData && allowAddEntry && (
                        <div className={'buttons-flex-wrapper'}>
                            <div>
                                <Button
                                    label='Add entry'
                                    className='p-button-sm p-button-success'
                                    icon='pi pi-plus'
                                    onClick={() => onClickAddEntry()}
                                    disabled={createNewEntryStatus === 'pending' || (selectedEntryId !== '' && selectedEntryIndex != null)}
                                    loading={createNewEntryStatus === 'pending'}
                                />
                            </div>
                            <div>
                                <ExportDataTableButton header={configData} body={templateData}
                                                       tableName={props.safeName}/>
                            </div>
                        </div>
                    )}
                </div>

                {templateData ? (
                    templateData.length > 0 ? (
                        transposed ? (
                            props.reportOptions?.splitAfter && templateData.length > props.reportOptions?.splitAfter ? (
                                printSplitColumns()
                            ) : (
                                <ProductTemplateTableTransposed
                                    configData={configData}
                                    templateData={templateData}
                                    editedDataHandler={editedDataHandler}
                                    selectedEntryBody={selectedEntryBody}
                                    selectedEntryIndex={selectedEntryIndex}
                                    selectedEntryId={selectedEntryId}
                                    onClickSign={onClickSign}
                                    handleTableKeyDown={handleTableKeyDown}
                                    EditRowDataCell={EditRowDataCell}
                                    itemsPerPage={props.itemsPerPage}
                                    page={page}
                                    setPage={setPage}
                                    reportOptions={props.reportOptions}
                                />
                            )
                        ) : props.reportOptions?.splitAfter && templateData.length > props.reportOptions?.splitAfter ? (
                            printSplitRows()
                        ) : (
                            <ProductTemplateTableDefault
                                configData={configData}
                                templateData={templateData}
                                editedDataHandler={editedDataHandler}
                                selectedEntryBody={selectedEntryBody}
                                selectedEntryIndex={selectedEntryIndex}
                                selectedEntryId={selectedEntryId}
                                onClickSign={onClickSign}
                                handleTableKeyDown={handleTableKeyDown}
                                EditRowDataCell={EditRowDataCell}
                                page={page}
                                setPage={setPage}
                                itemsPerPage={props.itemsPerPage}
                                reportOptions={props.reportOptions}
                                editColumnLocation={props.editColumnLocation}
                                allowRowEdit={allowRowEdit}
                            />
                        )
                    ) : (
                        <div className='demo-container p-p-4'>
                            <Error text={'No data'}/>
                        </div>
                    )
                ) : (
                    <ErrorOnTemplateBody/>
                )}
            </div>
        </div>
    );
}

interface IError {
    text: string;
}

function Error(props: IError) {
    return (
        <div style={{margin: 'auto', width: '50%', padding: '10px'}}>
            <div className='demo-container p-p-4' style={{border: '2px solid #3C76DA', borderRadius: '25px'}}>
                <h3 className='p-d-block p-mx-auto' style={{textAlign: 'center'}}>
                    {props.text}
                </h3>
            </div>
        </div>
    );
}
